import {
  getModule, Module, VuexModule, Mutation, Action,
} from 'vuex-module-decorators';
import store from '@/store/store';
import BurnRedeemExtensionContractCore, { BurnRedeemOnChain } from '@/classes/burnRedeemExtensionCore';
import Creator1155Contract from '@/classes/creator1155Contract';
import Creator721Contract from '@/classes/creator721Contract';
import { BurnRedeemStudioInstance, ContractSpec } from '@/api/models/burnRedeem';
import apiHelpers from '@/api/apiHelpers';
import { MOCK_BR } from '../../../dev/mock-br'; // eslint-disable-line
import BurnRedeemExtensionContract721V3 from '@/classes/burnRedeemExtensionContract721V3';
import BurnRedeemExtensionContract721V2 from '@/classes/burnRedeemExtensionContract721V2';
import BurnRedeemExtensionContract1155V3 from '@/classes/burnRedeemExtensionContract1155V3';
import BurnRedeemExtensionContract1155V2 from '@/classes/burnRedeemExtensionContract1155V2';
import BurnRedeemExtensionContract1155V1 from '@/classes/burnRedeemExtensionContract1155V1';
import { Version } from '@manifoldxyz/typed-contracts/dist/contract-versions/ContractVersion';
import { BigNumber } from '@ethersproject/bignumber';
import { BurnRedeem1155Version, BurnRedeem721Version } from '@manifoldxyz/typed-contracts/dist/contract-versions';
import { getFees } from '@/lib/util';

export interface IBRModuleState {
  extensionAddress: string;
  burnRedeemIndex: string;
  redeemContractAddress: string;
  burn: BurnRedeemOnChain | null;
  redeemSpec: ContractSpec | null;
  burnSpecs: ContractSpec[];
  extensionContract: BurnRedeemExtensionContractCore | null;
  extensionVersion: Version | null;
  extensionFees: {
    single: BigNumber;
    set: BigNumber;
  }
  redeemContract: Creator1155Contract | Creator721Contract | null;
  studioInstance: BurnRedeemStudioInstance| null;
  redeemAssetMetadata: {name: string; description: string; image: string;}
}

@Module({
  name: 'BR',
  store,
  dynamic: true,
})
class BRModule extends VuexModule implements IBRModuleState {
  public extensionAddress: IBRModuleState['extensionAddress'] = '';
  public burnRedeemIndex: IBRModuleState['burnRedeemIndex'] = '-1'; // -1 DNE
  public redeemContractAddress: IBRModuleState['redeemContractAddress'] = '';
  public burn: IBRModuleState['burn'] = null;
  public redeemSpec: IBRModuleState['redeemSpec'] = null;
  public burnSpecs: IBRModuleState['burnSpecs'] = [];
  public extensionContract: IBRModuleState['extensionContract'] = null;
  public extensionVersion: IBRModuleState['extensionVersion'] = null;
  public extensionFees: IBRModuleState['extensionFees'] = {
    single: BigNumber.from('0'),
    set: BigNumber.from('0'),
  };
  public redeemContract: IBRModuleState['redeemContract'] = null;
  public studioInstance: IBRModuleState['studioInstance'] = null;
  public redeemAssetMetadata: IBRModuleState['redeemAssetMetadata'] = {
    name: '',
    description: '',
    image: '',
  };

  get status(): 'not-started' | 'enabled' | 'ended' | null {
    const c = this.burn;
    const now = Date.now();

    if (!c) {
      return null;
    }

    if (c?.startDate && c.startDate * 1000 > now) {
      return 'not-started';
    } else if (c?.endDate && c.endDate * 1000 < now) {
      return 'ended';
    } else {
      return 'enabled';
    }
  }

  get contractsToApprove(): string[] {
    const contractsToApprove = [];
    if (this.burn?.burnSet) {
      for (let i = 0; i < this.burn.burnSet.length; i++) {
        contractsToApprove.push(this.burn.burnSet[i].items[0].contractAddress);
      }
    }
    return contractsToApprove;
  }

  @Mutation
  SET_EXTENSION_ADDRESS(extensionAddress: IBRModuleState['extensionAddress']): void {
    this.extensionAddress = extensionAddress;
  }

  @Mutation
  SET_BURN_REDEEM_INDEX(burnRedeemIndex: IBRModuleState['burnRedeemIndex']): void {
    this.burnRedeemIndex = burnRedeemIndex;
  }

  @Mutation
  SET_REDEEM_CONTRACT_ADDRESS(
    redeemContractAddress: IBRModuleState['redeemContractAddress'],
  ): void {
    this.redeemContractAddress = redeemContractAddress;
  }

  @Mutation
  SET_BURN(burn: IBRModuleState['burn']): void {
    this.burn = burn;
  }

  @Mutation
  SET_REDEEM_SPEC(redeemSpec: IBRModuleState['redeemSpec']): void {
    this.redeemSpec = redeemSpec;
  }

  @Mutation
  SET_BURN_SPECS(burnSpecs: IBRModuleState['burnSpecs']): void {
    /**
     * @dev
     *
     * One burn spec per burn group, as right now we force just one item per burn group.
     */
    this.burnSpecs = burnSpecs;
  }

  @Mutation
  SET_EXTENSION_CONTRACT(extensionContract: IBRModuleState['extensionContract']): void {
    this.extensionContract = extensionContract;
  }

  @Mutation
  SET_REDEEM_CONTRACT(redeemContract: IBRModuleState['redeemContract']): void {
    this.redeemContract = redeemContract;
  }

  @Mutation
  SET_EXTENSION_VERSION(extensionVersion: IBRModuleState['extensionVersion']): void {
    this.extensionVersion = extensionVersion;
  }

  @Mutation
  SET_EXTENSION_FEES(extensionFees: IBRModuleState['extensionFees']): void {
    this.extensionFees = extensionFees;
  }

  @Mutation
  SET_STUDIO_INSTANCE(studioInstance: IBRModuleState['studioInstance']): void {
    this.studioInstance = studioInstance;
  }

  @Mutation
  SET_REDEEM_ASSET_METADATA(redeemAssetMetadata: IBRModuleState['redeemAssetMetadata']): void {
    this.redeemAssetMetadata = redeemAssetMetadata;
  }

  @Action
  setExtensionAddressSpecVersionAndFees(extensionAddress: IBRModuleState['extensionAddress']): void {
    this.SET_EXTENSION_ADDRESS(extensionAddress);


    if (BurnRedeem721Version.isKnownAddress(extensionAddress)) {
      this.SET_REDEEM_SPEC('erc721');
      this.SET_EXTENSION_VERSION(
        BurnRedeem721Version.getVersionByAddress(extensionAddress),
      );

      if (this.extensionVersion) {
        this.SET_EXTENSION_FEES(
          getFees(
            this.extensionVersion,
          ),
        );
      }
    }

    if (BurnRedeem1155Version.isKnownAddress(extensionAddress)) {
      this.SET_REDEEM_SPEC('erc1155');
      this.SET_EXTENSION_VERSION(
        BurnRedeem1155Version.getVersionByAddress(extensionAddress),
      );

      if (this.extensionVersion) {
        this.SET_EXTENSION_FEES(
          getFees(
            this.extensionVersion,
          ),
        );
      }
    }
  }

  @Action
  setBurnRedeemIndex(burnRedeemIndex: IBRModuleState['burnRedeemIndex']): void {
    this.SET_BURN_REDEEM_INDEX(burnRedeemIndex);
  }

  @Action
  setRedeemContractAddress(redeemContractAddress: IBRModuleState['redeemContractAddress']): void {
    this.SET_REDEEM_CONTRACT_ADDRESS(redeemContractAddress);
  }

  @Action
  setBurn(burn: IBRModuleState['burn']): void {
    this.SET_BURN(burn);
  }

  @Action
  setRedeemSpec(redeemSpec: IBRModuleState['redeemSpec']): void {
    this.SET_REDEEM_SPEC(redeemSpec);
  }

  @Action
  setBurnSpec(burnSpecs: IBRModuleState['burnSpecs']): void {
    this.SET_BURN_SPECS(burnSpecs);
  }

  @Action
  setExtensionContract(extensionContract: IBRModuleState['extensionContract']): void {
    this.SET_EXTENSION_CONTRACT(extensionContract);
  }

  @Action
  setStudioInstance(studioInstance: IBRModuleState['studioInstance']): void {
    this.SET_STUDIO_INSTANCE(studioInstance);
  }

  @Action
  setRedeemAssetMetadata(redeemAssetMetadata: IBRModuleState['redeemAssetMetadata']): void {
    this.SET_REDEEM_ASSET_METADATA(redeemAssetMetadata);
  }

  @Action
  async refreshRedeemAssetData (): Promise<void> {
    try {
      let baseMetadataURL = '';
      if (this.burn?.storageProtocol === 2) {
        baseMetadataURL = 'https://arweave.net/';
      } else if (this.burn?.storageProtocol === 3) {
        baseMetadataURL = 'https://ipfs.io/ipfs/';
      }

      const response = await fetch(baseMetadataURL + this.burn?.location);
      this.setRedeemAssetMetadata(await response.json());
    } catch {
      // fallback to public instance data
      this.setRedeemAssetMetadata( {
        name: this.studioInstance?.publicData?.name || '',
        description: this.studioInstance?.publicData?.description || '',
        image: this.studioInstance?.publicData?.redeemAssetMedia || this.studioInstance?.publicData?.image || '',
      });
    }
  }

  @Action
  async refreshStudioInstance(instanceId: string): Promise<void> {
    if (instanceId) {
      const value = BurnRedeemStudioInstance.fromJson(
        await apiHelpers.fetch(`/public/instance/data?id=${instanceId}`),
      );
      this.setStudioInstance(value);
      return;
    }
  }

  @Action
  async refreshBurnRedeemOnChain(): Promise<void> {
    try {
      let burn = null;
      if (this.extensionAddress && this.redeemContractAddress && this.burnRedeemIndex) {
        if (this.redeemSpec === 'erc721') {
          if (!this.extensionContract && this.extensionVersion === 'v2.0') {
            this.setExtensionContract(new BurnRedeemExtensionContract721V2(this.extensionAddress));
          }

          if (!this.extensionContract
            && (this.extensionVersion === 'v3.0' || this.extensionVersion === 'v3.1')
          ) {
            this.setExtensionContract(new BurnRedeemExtensionContract721V3(this.extensionAddress));
          }
        } else {
          if (!this.extensionContract && this.extensionVersion === 'v1.0') {
            this.setExtensionContract(new BurnRedeemExtensionContract1155V1(this.extensionAddress));
          }

          if (!this.extensionContract && this.extensionVersion === 'v2.0') {
            this.setExtensionContract(new BurnRedeemExtensionContract1155V2(this.extensionAddress));
          }

          if (!this.extensionContract
            && (this.extensionVersion === 'v3.0' || this.extensionVersion === 'v3.1')
          ) {
            this.setExtensionContract(new BurnRedeemExtensionContract1155V3(this.extensionAddress));
          }
        }

        // @dev: this call is allowlisted by the Manifold Provider Eth Node
        // that way we can make this call before we user has connected their wallet
        if (this.extensionContract) {
          burn = await this.extensionContract.getBurnRedeem(
            this.redeemContractAddress,
            this.burnRedeemIndex,
          );
        }

        // find out if ?dev=true is in the URL
        const urlParams = new URLSearchParams(window.location.search);
        const dev = urlParams.get('dev');

        // @dev: for local testing without a real instance, uncomment and modify the mock definition
        // @dev: also helps find out where where the "souce of truth" for certain data is
        if (process.env.NODE_ENV === 'development' && dev) {
          burn = MOCK_BR;
        }

        if (!burn) {
          throw new Error('Burn DNE on network');
        }

        this.SET_BURN(burn);

        if (burn) {
          const specs: ContractSpec[] = [];
          for (let i = 0; i < burn?.burnSet.length; i++) {
            burn.burnSet[i].items[0].tokenSpec === 1 ? specs.push('erc721') : specs.push('erc1155');
          }
          this.SET_BURN_SPECS(specs);
        }
      } else {
        throw Error(
          'No extension address, redeem contract address, and/or burn redeem index given',
        );
      }
    } catch (e) {
      console.warn(e);
      this.SET_BURN(null);
      this.SET_BURN_SPECS([]);
      throw e;
    }
  }
}

export const ModuleBRModule = getModule(BRModule);
